/**
 * Image Cache Utility - Enhanced with Persistent Storage
 * 
 * Provides persistent image caching that survives page refreshes and navigation.
 * Uses browser's Cache API for persistent storage combined with in-memory cache for performance.
 */

class ImageCache {
  constructor() {
    this.cache = new Map(); // In-memory cache for immediate access
    this.loadingPromises = new Map();
    this.refCounts = new Map(); // Track how many components are using each image
    this.persistentCache = null; // Will be initialized async
    this.cacheName = 'irish-funds-image-cache-v1';
    this.initializePersistentCache();
  }

  /**
   * Initialize the persistent cache using Cache API
   */
  async initializePersistentCache() {
    try {
      if ('caches' in window) {
        this.persistentCache = await caches.open(this.cacheName);

        
        // Clean up expired cached images on startup
        this.cleanupOldCachedImages();
      } else {
        // continue
      }
    } catch (error) {
      console.warn('[ImageCache] Failed to initialize persistent cache:', error);
    }
  }

  /**
   * Clean up expired cached images (run on startup)
   */
  async cleanupOldCachedImages() {
    if (!this.persistentCache) {
      return;
    }

    try {
      const keys = await this.persistentCache.keys();
      const maxAge = 30 * 60 * 1000; // 30 minutes in milliseconds
      const now = Date.now();
      
      for (const key of keys) {
        try {
          const response = await this.persistentCache.match(key);
          if (response) {
            // const cacheControl = response.headers.get('cache-control');
            const dateHeader = response.headers.get('date');
            
            if (dateHeader) {
              const cacheDate = new Date(dateHeader).getTime();
              if (now - cacheDate > maxAge) {
                await this.persistentCache.delete(key);
              }
            }
          }
        } catch (error) {
          // If there's an error with a specific key, just continue
          console.warn(`[ImageCache] Error checking cache age for ${key.url}:`, error);
        }
      }
    } catch (error) {
      console.warn('[ImageCache] Error during cache cleanup:', error);
    }
  }

  /**
   * Get image from persistent cache
   * @param {string} imageKey 
   * @returns {Promise<string|null>}
   */
  async getFromPersistentCache(imageKey) {
    if (!this.persistentCache) {
      return null;
    }

    try {
      const request = new Request(`https://cache.local/${imageKey}`);
      const response = await this.persistentCache.match(request);
      
      if (response) {
        // Check if cache is still valid (30 minutes)
        const dateHeader = response.headers.get('date');
        if (dateHeader) {
          const cacheDate = new Date(dateHeader).getTime();
          const now = Date.now();
          const maxAge = 30 * 60 * 1000; // 30 minutes in milliseconds
          
          if (now - cacheDate > maxAge) {
            // Cache expired, remove it
            await this.persistentCache.delete(request);
            return null;
          }
        }
        
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        
        // Store in memory cache for immediate future access
        this.cache.set(imageKey, imageUrl);
        return imageUrl;
      }
    } catch (error) {
      console.warn(`[ImageCache] Error reading from persistent cache for ${imageKey}:`, error);
    }
    
    return null;
  }

  /**
   * Store image in persistent cache
   * @param {string} imageKey 
   * @param {string} imageUrl 
   */
  async storeToPersistentCache(imageKey, imageUrl) {
    if (!this.persistentCache || !imageUrl || !imageUrl.startsWith('blob:')) {
      return;
    }

    try {
      // Convert blob URL back to blob for storage
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Create a Response object for the cache
      const cacheResponse = new Response(blob, {
        headers: {
          'Content-Type': blob.type || 'image/jpeg',
          'Cache-Control': 'max-age=1800', // Cache for 30 minutes
          'Date': new Date().toISOString() // Add timestamp for cleanup
        }
      });
      
      const request = new Request(`https://cache.local/${imageKey}`);
      await this.persistentCache.put(request, cacheResponse);
    } catch (error) {
      console.warn(`[ImageCache] Error storing to persistent cache for ${imageKey}:`, error);
    }
  }

  /**
   * Get a cached image or load it if not cached
   * @param {string} imageKey - Unique key for the image (e.g., article.id or imageUrl)
   * @param {Function} loadFunction - Function that returns a promise to load the image
   * @returns {Promise<string>} Promise resolving to image URL
   */
  async getImage(imageKey, loadFunction) {
    // 1. Check in-memory cache first (fastest)
    if (this.cache.has(imageKey)) {
      this.incrementRefCount(imageKey);
      return this.cache.get(imageKey);
    }

    // 2. Check if currently loading
    if (this.loadingPromises.has(imageKey)) {
      const result = await this.loadingPromises.get(imageKey);
      this.incrementRefCount(imageKey);
      return result;
    }

    // 3. Check persistent cache
    const persistentImage = await this.getFromPersistentCache(imageKey);
    if (persistentImage) {
      this.incrementRefCount(imageKey);
      return persistentImage;
    }

    // 4. Load from network
    const loadingPromise = loadFunction()
      .then(async imageUrl => {
        // Check if imageUrl is valid
        if (!imageUrl) {
          throw new Error('Load function returned null or undefined URL');
        }
        
        // Store in memory cache
        this.cache.set(imageKey, imageUrl);
        
        // Store in persistent cache (async, don't wait) - only for blob URLs
        if (imageUrl.startsWith('blob:')) {
          this.storeToPersistentCache(imageKey, imageUrl);
        }
        
        this.loadingPromises.delete(imageKey);
        this.incrementRefCount(imageKey);
        return imageUrl;
      })
      .catch(error => {
        this.loadingPromises.delete(imageKey);
        throw error;
      });

    this.loadingPromises.set(imageKey, loadingPromise);
    return loadingPromise;
  }

  /**
   * Increment reference count for an image
   * @param {string} imageKey 
   */
  incrementRefCount(imageKey) {
    const currentCount = this.refCounts.get(imageKey) || 0;
    this.refCounts.set(imageKey, currentCount + 1);
  }

  /**
   * Decrement reference count for an image and cleanup if no longer needed
   * @param {string} imageKey 
   */
  decrementRefCount(imageKey) {
    const currentCount = this.refCounts.get(imageKey) || 0;
    const newCount = Math.max(0, currentCount - 1);
    
    if (newCount === 0) {
      // No more references, cleanup memory cache after a delay
      // Keep persistent cache for longer
      setTimeout(() => {
        if (this.refCounts.get(imageKey) === 0) {
          this.removeFromMemoryCache(imageKey);
        }
      }, 30000); // Keep in memory for 30 seconds after last reference
    }
    
    this.refCounts.set(imageKey, newCount);
  }

  /**
   * Remove an image from memory cache only (keep persistent cache)
   * @param {string} imageKey 
   */
  removeFromMemoryCache(imageKey) {
    const imageUrl = this.cache.get(imageKey);
    if (imageUrl && imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl);
    }
    this.cache.delete(imageKey);
    this.refCounts.delete(imageKey);
  }

  /**
   * Remove an image from both memory and persistent cache
   * @param {string} imageKey 
   */
  async removeImage(imageKey) {
    // Remove from memory cache
    this.removeFromMemoryCache(imageKey);
    
    // Remove from persistent cache
    if (this.persistentCache) {
      try {
        const request = new Request(`https://cache.local/${imageKey}`);
        await this.persistentCache.delete(request);
      } catch (error) {
        console.warn(`[ImageCache] Error removing from persistent cache for ${imageKey}:`, error);
      }
    }
  }

  /**
   * Get cached image URL without loading (checks both memory and persistent cache)
   * @param {string} imageKey 
   * @returns {string|null}
   */
  getCachedImage(imageKey) {
    return this.cache.get(imageKey) || null;
  }

  /**
   * Check if image is currently loading
   * @param {string} imageKey 
   * @returns {boolean}
   */
  isLoading(imageKey) {
    return this.loadingPromises.has(imageKey);
  }

  /**
   * Clear all cached images (memory only, keep persistent cache)
   */
  clearMemoryCache() {
    // Cleanup all blob URLs
    for (const imageUrl of this.cache.values()) {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    }
    this.cache.clear();
    this.loadingPromises.clear();
    this.refCounts.clear();
  }

  /**
   * Clear all cached images including persistent cache
   */
  async clearAll() {
    // Clear memory cache
    this.clearMemoryCache();
    
    // Clear persistent cache
    if (this.persistentCache) {
      try {
        const keys = await this.persistentCache.keys();
        await Promise.all(keys.map(key => this.persistentCache.delete(key)));
      } catch (error) {
        console.warn('[ImageCache] Error clearing persistent cache:', error);
      }
    }
  }

  /**
   * Check and clean up expired images from persistent cache
   */
  async cleanupExpiredImages() {
    await this.cleanupOldCachedImages();
  }

  /**
   * Get cache statistics
   * @returns {Object}
   */
  getStats() {
    return {
      memoryCache: this.cache.size,
      loadingImages: this.loadingPromises.size,
      totalReferences: Array.from(this.refCounts.values()).reduce((sum, count) => sum + count, 0),
      persistentCacheAvailable: !!this.persistentCache
    };
  }

  /**
   * Get persistent cache statistics
   * @returns {Promise<Object>}
   */
  async getPersistentStats() {
    if (!this.persistentCache) {
      return { persistentCacheSize: 0 };
    }

    try {
      const keys = await this.persistentCache.keys();
      return {
        persistentCacheSize: keys.length
      };
    } catch (error) {
      console.warn('[ImageCache] Error getting persistent cache stats:', error);
      return { persistentCacheSize: 0 };
    }
  }
}

// Create a singleton instance
const imageCache = new ImageCache();

// Cleanup memory cache on page unload (keep persistent cache)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    imageCache.clearMemoryCache();
  });
  
  // Expose debug helper to window for console access (development only)
  if (process.env.NODE_ENV === 'development') {
    window.imageCacheDebug = {
      async getStats() {
        const memoryStats = imageCache.getStats();
        const persistentStats = await imageCache.getPersistentStats();
        return { ...memoryStats, ...persistentStats };
      },
      
      async clearMemoryCache() {
        imageCache.clearMemoryCache();
      },
      
      async clearAll() {
        await imageCache.clearAll();
      },
      
      async cleanupExpired() {
        await imageCache.cleanupExpiredImages();
      },
      
      async listCachedImages() {
        const memoryKeys = Array.from(imageCache.cache.keys());
        
        let persistentPaths = [];
        if (imageCache.persistentCache) {
          try {
            const persistentKeys = await imageCache.persistentCache.keys();
            persistentPaths = persistentKeys.map(key => key.url.replace('https://cache.local/', ''));
          } catch (error) {
            console.warn('Error listing persistent cache keys:', error);
          }
        }
        
        return {
          memoryKeys,
          persistentKeys: persistentPaths,
          persistentSupported: !!imageCache.persistentCache
        };
      }
    };
  }
}

export default imageCache; 